import { useQuery } from "@tanstack/react-query";

import { api } from "./ApiClient";

import { CookiesHelper } from "../helpers/cookies.helper";
import { AuthHelper } from "../helpers/auth.helper";

import { EtySimulationStage } from "../types/entities.interface";
import { ESimulationKey, ESimulationStageKey } from "../types/entities.enum";
import { ISimulationWithStagesAndStatus, ISimulationWithStatus } from "../types/services.interface";

export const useGetSimulations = () => {
    const isSessionTokenValid = AuthHelper.isSessionTokenValid().result;

    const queryFn = async (): Promise<ISimulationWithStatus[]> => {
        const token = CookiesHelper.get(`${process.env.REACT_APP__SESSION_TOKEN_NAME}`);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const res = await api.get(`/simulations`);
        return res.data?.length ? res.data : [];
    };

    return useQuery({
        queryKey: ['simulations'],
        queryFn,
        retry: false,
        enabled: isSessionTokenValid,
    });
};

export const useGetSimulationWithStages = (simulationKey: ESimulationKey | null) => {
    const isSessionTokenValid = AuthHelper.isSessionTokenValid().result;

    const queryFn = async (): Promise<ISimulationWithStagesAndStatus> => {
        const token = CookiesHelper.get(`${process.env.REACT_APP__SESSION_TOKEN_NAME}`);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const res = await api.get(`/simulations/details/${simulationKey}`);
        return res.data;
    };

    return useQuery({
        queryKey: ['simulation-with-stages', simulationKey],
        queryFn,
        retry: false,
        enabled: isSessionTokenValid && !!simulationKey,
    });
};

export const useGetSimulationStageDetails = (simulationKey: ESimulationKey | null, stageKey: ESimulationStageKey | null) => {
    const isSessionTokenValid = AuthHelper.isSessionTokenValid().result;

    const queryFn = async (): Promise<EtySimulationStage> => {
        const token = CookiesHelper.get(`${process.env.REACT_APP__SESSION_TOKEN_NAME}`);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const res = await api.get(`/simulations/stage-details/${simulationKey}/${stageKey}`);
        return res.data;
    };

    return useQuery({
        queryKey: ['simulation-stage-details', simulationKey, stageKey],
        queryFn,
        retry: false,
        enabled: isSessionTokenValid && !!simulationKey && !!stageKey,
    });
};
